import {useCallback, useEffect, useMemo} from 'react';
import {
    useFetchProblemsOfTheDayQuery,
    useFetchProblemsOfTheDayStatusQuery,
} from '@api';
import {
    DailyProblemContentResponse,
    DailyProblemStatusResponse,
    ProblemOfTheDayResponse,
} from '@types';
import {useSelector} from './common';

export type UseProblemsOfTheDayOptions = {
    skip?: boolean;
};

const mergeDailyProblems = (
    content?: DailyProblemContentResponse,
    status?: DailyProblemStatusResponse
): ProblemOfTheDayResponse | undefined => {
    if (!content) return undefined;

    const statusByProblemId = new Map(
        status?.entries.map(entry => [entry.problemId, entry]) ?? []
    );

    return {
        currentStreak: status?.currentStreak,
        entries: content.entries.map(entry => {
            const entryStatus = statusByProblemId.get(entry.id);

            if (!entryStatus) {
                return entry;
            }

            return {
                ...entry,
                solversCount: entryStatus.solversCount,
                solvedByCurrentUser: entryStatus.solvedByCurrentUser,
                firstSolveAttemptFailedByCurrentUser:
                    entryStatus.firstSolveAttemptFailedByCurrentUser,
                currentStreak: entryStatus.currentStreak,
                isSpeedPlay: entryStatus.isSpeedPlay,
                isSpeedSolved: entryStatus.isSpeedSolved,
            };
        }),
        resetsAt: content.resetsAt,
        streakRecovery: status?.streakRecovery,
    };
};

export const useProblemsOfTheDay = ({
    skip = false,
}: UseProblemsOfTheDayOptions = {}) => {
    const {user} = useSelector(state => state.auth);
    const contentQuery = useFetchProblemsOfTheDayQuery(undefined, {
        skip,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
    });
    const statusQuery = useFetchProblemsOfTheDayStatusQuery(
        user?.id ?? null,
        {
            skip,
            refetchOnFocus: true,
            refetchOnMountOrArgChange: true,
        }
    );
    const currentData = useMemo(
        () =>
            mergeDailyProblems(
                contentQuery.currentData,
                statusQuery.currentData
            ),
        [contentQuery.currentData, statusQuery.currentData]
    );
    const data = useMemo(
        () => mergeDailyProblems(contentQuery.data, statusQuery.data),
        [contentQuery.data, statusQuery.data]
    );
    const resetsAt =
        contentQuery.currentData?.resetsAt ?? contentQuery.data?.resetsAt;
    const refetchContent = contentQuery.refetch;
    const refetchStatus = statusQuery.refetch;

    useEffect(() => {
        if (skip || !resetsAt) return;

        const resetTimestamp = Date.parse(resetsAt);
        if (!Number.isFinite(resetTimestamp)) return;

        const timer = window.setTimeout(() => {
            void refetchContent();
            void refetchStatus();
        }, Math.max(0, resetTimestamp - Date.now() + 1000));

        return () => window.clearTimeout(timer);
    }, [refetchContent, refetchStatus, resetsAt, skip]);

    const refetch = useCallback(
        () => Promise.all([refetchContent(), refetchStatus()]),
        [refetchContent, refetchStatus]
    );

    return {
        data,
        currentData,
        isLoading: contentQuery.isLoading || statusQuery.isLoading,
        isContentLoading: contentQuery.isLoading,
        isStatusLoading: statusQuery.isLoading,
        hasStatusData: statusQuery.currentData !== undefined,
        isFetching: contentQuery.isFetching || statusQuery.isFetching,
        isError: contentQuery.isError || statusQuery.isError,
        refetch,
        refetchStatus,
    };
};
